import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { cleanDuplicates } from '../../../utils/data';

// Helper interface for the segregated data structure
export interface DashboardData {
    details: any; // Breakdown details
    summary: any; // High level KPIs (for Project view)
    graphData: any[];
    lowerDetails: any[];
    tabsCache: Record<string, any[]>;
    loading: boolean;
}

const initialDashboardState: DashboardData = {
    details: {},
    summary: {},
    graphData: [],
    lowerDetails: [],
    tabsCache: {},
    loading: false
};

export interface ProjectsState {
    loadingTrees: boolean;
    trees: {
        pc: any[];
        pcParts: number[];
        de: any[];
        deParts: number[];
        fr: any[];
        frParts: number[];
    };

    meansTree: any[];
    meansTreeParts: number[];

    // Segregated Data States
    projectData: DashboardData;
    classificationData: DashboardData;
    resourceData: DashboardData;

    // Graph Modal Data
    graphModal: {
        list: any[];
        listPages: number;
        resources: any[];
        resourcesPages: number;
        loading: boolean;
        itemsPerPage: number;
    };

    // View Context
    selectedProject: string | null;
    projectSummary: any;

    // Global Loading/Error
    loading: boolean;
    error: string | null;
    dates: {CodPeriodo: string, DesPeriodo: string}[];
    datesIsLoading: boolean;
    datesParts: number[];
}

const initialState: ProjectsState = {
    loadingTrees: false,
    trees: {
        pc: [],
        pcParts: [],
        de: [],
        deParts: [],
        fr: [],
        frParts: [],
    },
    meansTree: [],
    meansTreeParts: [],

    projectData: { ...initialDashboardState },
    classificationData: { ...initialDashboardState },
    resourceData: { ...initialDashboardState },

    graphModal: {
        list: [],
        listPages: 0,
        resources: [],
        resourcesPages: 0,
        loading: false,
        itemsPerPage: 10,
    },

    selectedProject: null,
    projectSummary: null,
    loading: false,
    error: null,
    dates: [],
    datesIsLoading: false,
    datesParts: [],
};

export const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        setSelectedProject: (state, action: PayloadAction<string>) => {
            state.selectedProject = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        // Tree Setters
        setLoadingTrees: (state, { payload }: PayloadAction<boolean>) => {
            state.loadingTrees = payload;
        },
        setTreePC: (state, { payload }: PayloadAction<any>) => {
            if (state.trees.pcParts.includes(payload.part)) {
                return state;
            }
            state.trees.pcParts.push(payload.part)
            state.trees.pc = [...state.trees.pc, ...payload.data];
        },
        setTreeDE: (state, { payload }: PayloadAction<any>) => {
            if (state.trees.deParts.includes(payload.part)) {
                return state;
            }
            state.trees.deParts.push(payload.part)
            state.trees.de = [...state.trees.de, ...payload.data];
        },
        setTreeFR: (state, { payload }: PayloadAction<any>) => {
            if (state.trees.frParts.includes(payload.part)) {
                return state;
            }
            state.trees.frParts.push(payload.part)
            state.trees.fr = [...state.trees.fr, ...payload.data];
        },
        setMeansTree: (state, { payload }: PayloadAction<any>) => {
            if (state.meansTreeParts.includes(payload.part)) {
                return state;
            }
            state.meansTreeParts.push(payload.part)
            state.meansTree = [...state.meansTree, ...payload.data];
        },

        // Project Data Setters
        setProjectDetails: (state, { payload }: PayloadAction<any>) => {
            state.projectData.details = payload.data?.length ? payload.data[0] : {};
        },
        setProjectSummary: (state, { payload }: PayloadAction<any>) => {
            console.log('----- setProjectSummary', payload.data)
            state.projectData.summary = payload.data?.length ? payload.data[0] : {};
        },
        setProjectGraph: (state, { payload }: PayloadAction<any>) => {
            state.projectData.graphData = payload.data;
        },

        // Classification Data Setters
        resetClassificationData: (state) => {
            state.classificationData = { ...initialDashboardState };
        },
        setClassificationDetails: (state, { payload }: PayloadAction<any>) => {
            state.classificationData.details = payload.data?.length ? payload.data[0] : {};
        },
        setClassificationGraph: (state, { payload }: PayloadAction<any>) => {
            state.classificationData.graphData = payload.data;
        },
        setClassificationSummary: (state, { payload }: PayloadAction<any>) => {
            let finalData = {}
            if (payload.data?.length) {
                const data = payload.data[0];
                // console.log('state.classificationData.summary', data)
                finalData = {
                    CodProyecto: state.projectData.summary.CodProyecto,
                    Proyecto: state.projectData.summary.Proyecto,
                    Moneda: state.projectData.summary.Moneda,
                    FechaInicioReal: state.projectData.summary.FechaInicioReal,
                    FechaFinReal: state.projectData.summary.FechaFinReal,
                    FechaProcesoRO: state.projectData.summary.FechaProcesoRO,
                    ...data,
                }
            }
            state.classificationData.summary = finalData
        },

        // Resource Data Setters
        resetResourceData: (state) => {
            state.resourceData = { ...initialDashboardState };
        },
        setResourceLowerDetail: (state, { payload }: PayloadAction<any>) => {
            state.resourceData.lowerDetails = payload.data;
        },
        setResourceGraph: (state, { payload }: PayloadAction<any>) => {
            state.resourceData.graphData = payload.data;
        },
        setResourceSummary: (state, { payload }: PayloadAction<any>) => {
            let finalData = {}
            if (payload.data?.length) {
                const data = payload.data[0];
                // console.log('state.resourceData.summary', data)
                finalData = {
                    CodProyecto: state.projectData.summary.CodProyecto,
                    Proyecto: state.projectData.summary.Proyecto,
                    Moneda: state.projectData.summary.Moneda,
                    FechaInicioReal: state.projectData.summary.FechaInicioReal,
                    FechaFinReal: state.projectData.summary.FechaFinReal,
                    FechaProcesoRO: state.projectData.summary.FechaProcesoRO,
                    PadreDescripcion: state.classificationData.summary.Descripcion,
                    ...data,
                }
            }
            state.resourceData.summary = finalData
        },



        setResourceDetails: (state, action: PayloadAction<any>) => {
            state.resourceData.details = action.payload;
        },




        setProjectDataLoading: (state, action: PayloadAction<boolean>) => {
            state.projectData.loading = action.payload;
        },

        // Classification Data Setters
        setClassificationDataLoading: (state, action: PayloadAction<boolean>) => {
            state.classificationData.loading = action.payload;
        },

        // Resource Data Setters
        setResourceDataLoading: (state, action: PayloadAction<boolean>) => {
            state.resourceData.loading = action.payload;
        },

        // graph project details

        setGraphModalList: (state, {payload}: PayloadAction<any>) => {
            state.graphModal.listPages =  Math.ceil(payload.totalItems / state.graphModal.itemsPerPage);
            state.graphModal.list = payload.data;
        },
        appendGraphModalList: (state, {payload}: PayloadAction<any>) => {
            state.graphModal.listPages =  Math.ceil(payload.totalItems / state.graphModal.itemsPerPage);
            state.graphModal.list = [...state.graphModal.list, ...payload.data];
        },
        setGraphModalResources: (state, {payload}: PayloadAction<any>) => {
            state.graphModal.resourcesPages = Math.ceil(payload.totalItems / state.graphModal.itemsPerPage);
            state.graphModal.resources = payload.data;
        },
        appendGraphModalResources: (state, {payload}: PayloadAction<any>) => {
            state.graphModal.resourcesPages = Math.ceil(payload.totalItems / state.graphModal.itemsPerPage);
            state.graphModal.resources = [...state.graphModal.resources, ...payload.data];
        },
        resetGraphModal: (state) => {
            state.graphModal = {
                list: [],
                listPages: 0,
                resources: [],
                resourcesPages: 0,
                loading: false,
                itemsPerPage: 20,
            };
        },
        restartDates: (state) => {
            state.dates = [];
            state.datesParts = [];
            state.datesIsLoading = false;
        },
        setDates: (state, {payload}: PayloadAction<any>) => {
            if (state.datesParts.includes(payload.part)) {
                return state;
            }
            state.datesParts = [...state.datesParts, payload.part];
            state.datesIsLoading = state.datesParts.length !== payload.count;
            state.dates = cleanDuplicates([...state.dates, ...payload.data], 'CodPeriodo')
                            .sort((a, b) => 
                                -a.CodPeriodo.localeCompare(b.CodPeriodo)
                            )
                            .map(d => ({...d, Anio: d.CodPeriodo.slice(0, 4)}));
        },


        projectsReset: (state) => {
            return initialState;
        }
    },
});

export const {
    setSelectedProject,
    setLoading,
    setError,
    setLoadingTrees,
    setTreePC,
    setTreeDE,
    setTreeFR,
    setMeansTree,
    setProjectDetails,
    setProjectSummary,
    setProjectGraph,

    setClassificationDetails,
    setClassificationGraph,
    setClassificationSummary,


    resetResourceData,
    setResourceSummary,


    setProjectDataLoading,
    setClassificationDataLoading,
    resetClassificationData,
    setResourceDetails,
    setResourceGraph,
    setResourceLowerDetail,
    setResourceDataLoading,

    setGraphModalList,
    appendGraphModalList,
    setGraphModalResources,
    appendGraphModalResources,
    resetGraphModal,

    projectsReset,
    restartDates,
    setDates,
} = projectsSlice.actions;

export default projectsSlice.reducer;
